import {
  CategoryName,
  DisplayModeName,
  IDisplayModeParsed,
  TDatasetName,
  tg,
} from '@infotech-soe/metaschema';

import CachedSchemaRepository from '../../../infrastructure/schemas/storage/CachedSchemaRepository';

import DatasetTreeFactory from './DatasetTreeFactory';
import DatasetTreeNode from './logical/DatasetTreeNode';
import DisplayModeDatasetTreeNode from './logical/DisplayModeDatasetTreeNode';

/**
 * Concrete implementation of {@link DatasetTreeFactory} for display modes.
 */
class DisplayModeDatasetTreeFactory implements DatasetTreeFactory {
  private displayModeSchema: IDisplayModeParsed | null = null;

  constructor(
    private categoryName: CategoryName,
    private displayModeName: DisplayModeName
  ) {}

  public async createDatasetTree(): Promise<DatasetTreeNode> {
    const root = await this.findMasterDataset();
    return this.buildDatasetNode(root);
  }

  private async findMasterDataset(): Promise<TDatasetName> {
    const schema = await this.getDisplayModeSchema();

    for (const datasetName of Object.keys(schema.datasets)) {
      const dataset = schema.datasets[datasetName];

      if (!dataset.parent) {
        return datasetName;
      }
    }

    throw new Error('Invalid schema: no master dataset');
  }

  private async buildDatasetNode(
    datasetName: TDatasetName
  ): Promise<DisplayModeDatasetTreeNode> {
    const schema = await this.getDisplayModeSchema();
    const node = new DisplayModeDatasetTreeNode(datasetName);
    const { group, horizontalSlaveLayout } = schema.datasets[datasetName];

    if (tg.isSomething(group)) node.setGroup(group);

    if (tg.isSomething(horizontalSlaveLayout))
      node.setIsHorizontalSlaveLayout(horizontalSlaveLayout);

    for (const childDatasetName of Object.keys(schema.datasets)) {
      const childDataset = schema.datasets[childDatasetName];

      if (childDataset.parent === datasetName) {
        const childNode = await this.buildDatasetNode(childDatasetName);

        node.addChild(childNode);
      }
    }

    return node;
  }

  private async getDisplayModeSchema(): Promise<IDisplayModeParsed> {
    if (this.displayModeSchema !== null) {
      return this.displayModeSchema;
    }
    const schema = await new CachedSchemaRepository().getDisplayMode(
      this.categoryName,
      this.displayModeName
    );

    this.displayModeSchema = schema;

    return schema;
  }
}

export default DisplayModeDatasetTreeFactory;
